import React from "react";
import { Image, View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { FileText, MessageSquare, Plus } from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import { DocumentRow, MoonDateField } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { CancerTreatmentDetail } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { useFlowProgress } from "../../useFlowProgress";
import { getCancerTreatmentLabelKey } from "../options";
import { useMedicalHistoryTheme } from "../../../../theme/MedicalHistoryTheme";

const treatmentIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;

export function CancerTreatmentDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const progress = useFlowProgress("CancerTreatmentDetails");
  const theme = useMedicalHistoryTheme();

  const updateTreatment = (
    cancerIndex: number,
    treatmentIndex: number,
    updates: Partial<CancerTreatmentDetail>,
  ) => {
    const updatedCancers = state.cancers.map((cancer, ci) => {
      if (ci !== cancerIndex) return cancer;
      return {
        ...cancer,
        treatments: cancer.treatments.map((treatment, ti) =>
          ti === treatmentIndex ? { ...treatment, ...updates } : treatment,
        ),
      };
    });
    updateState({ cancers: updatedCancers });
  };

  const addAnotherTreatment = (cancerIndex: number) => {
    const updatedCancers = state.cancers.map((cancer, ci) => {
      if (ci !== cancerIndex) return cancer;
      return {
        ...cancer,
        treatments: [
          ...cancer.treatments,
          {
            treatmentType: cancer.treatments[0]?.treatmentType ?? "other",
          } as CancerTreatmentDetail,
        ],
      };
    });
    updateState({ cancers: updatedCancers });
  };

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf("CancerTreatmentDetails");
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  // Group treatments by cancer
  const cancersWithTreatments = state.cancers
    .map((cancer, index) => ({
      cancer,
      index,
      label:
        cancer.cancer ??
        t(
          "med_history.medical.disease_history.cancer_details.cancer_fallback",
          { params: { index: String(index + 1) } },
        ),
    }))
    .filter((c) => c.cancer.treatments.length > 0);

  return (
    <QuestionLayout
      title={t(
        "med_history.medical.disease_history.cancer_treatment_details.title",
      )}
      subtitle={t(
        "med_history.medical.disease_history.cancer_treatment_details.subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        {cancersWithTreatments.map(
          ({ cancer, index: cancerIndex, label: cancerLabel }) => (
            <View key={cancerIndex}>
              {cancersWithTreatments.length > 1 && (
                <Text
                  style={{
                    color: theme.textMuted,
                    fontSize: 14,
                    fontWeight: "500",
                    letterSpacing: 0.5,
                    marginBottom: 12,
                    paddingLeft: 4,
                  }}
                >
                  {cancerLabel}
                </Text>
              )}

              {cancer.treatments.map((treatment, treatmentIndex) => (
                <View
                  key={`${cancerIndex}-${treatmentIndex}`}
                  style={[GLASS_CARD_STYLE, styles.detailCard]}
                >
                  <View style={styles.cardHeader}>
                    <Text style={[styles.cardTitle, { color: theme.text }]}>
                      {treatment.treatmentType === "other"
                        ? t(
                            "med_history.medical.disease_history.cancer_treatment_details.other_section_label",
                          )
                        : t(
                            getCancerTreatmentLabelKey(treatment.treatmentType),
                          )}
                    </Text>
                  </View>

                  {treatment.treatmentType === "other" && (
                    <IconInput
                      icon={FileText}
                      label={t(
                        "med_history.medical.disease_history.cancer_treatment_details.other_name_label",
                      )}
                      placeholder={t(
                        "med_history.medical.disease_history.cancer_treatment_details.other_name_placeholder",
                      )}
                      value={treatment.other ?? ""}
                      onChangeText={(text) =>
                        updateTreatment(cancerIndex, treatmentIndex, {
                          other: text || null,
                        })
                      }
                    />
                  )}

                  <MoonDateField
                    label={t(
                      "med_history.medical.disease_history.cancer_treatment_details.start_label",
                    )}
                    placeholder={t(
                      "med_history.medical.disease_history.cancer_treatment_details.start_placeholder",
                    )}
                    value={treatment.startDate ?? ""}
                    onChange={(iso) =>
                      updateTreatment(cancerIndex, treatmentIndex, {
                        startDate: iso || null,
                      })
                    }
                  />

                  <MoonDateField
                    label={t(
                      "med_history.medical.disease_history.cancer_treatment_details.end_label",
                    )}
                    placeholder={t(
                      "med_history.medical.disease_history.cancer_treatment_details.end_placeholder",
                    )}
                    value={treatment.endDate ?? ""}
                    onChange={(iso) =>
                      updateTreatment(cancerIndex, treatmentIndex, {
                        endDate: iso || null,
                      })
                    }
                  />

                  <IconInput
                    icon={MessageSquare}
                    label={t(
                      "med_history.medical.disease_history.cancer_treatment_details.notes_label",
                    )}
                    placeholder={t(
                      "med_history.medical.disease_history.cancer_treatment_details.notes_placeholder",
                    )}
                    value={treatment.notes ?? ""}
                    onChangeText={(text) =>
                      updateTreatment(cancerIndex, treatmentIndex, {
                        notes: text || null,
                      })
                    }
                    multiline
                    numberOfLines={3}
                  />
                </View>
              ))}

              <DocumentRow
                onPress={() => addAnotherTreatment(cancerIndex)}
                accessibilityLabel={t(
                  "med_history.medical.disease_history.cancer_treatment_details.add_another",
                )}
                leading={
                  <Image
                    source={treatmentIllustration}
                    resizeMode="contain"
                    style={styles.addIllustration}
                  />
                }
                title={t(
                  "med_history.medical.disease_history.cancer_treatment_details.add_another",
                )}
                trailing={
                  <Plus
                    size={18}
                    color={EMBER.colors.textLight}
                    strokeWidth={1.5}
                  />
                }
                showChevron={false}
                style={styles.addRow}
              />
            </View>
          ),
        )}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 24,
  },
  detailCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    gap: 16,
    marginBottom: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});
